// src/pages/BusinessSelectPage.tsx
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    Button,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchAdminMe, selectBusiness } from "../auth/auth";

export default function BusinessSelectPage() {
    const { t } = useTranslation();
    const nav = useNavigate();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState<number | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [items, setItems] = React.useState<
        { id: number; name: string; slug: string; role: string }[]
    >([]);

    React.useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const me = await fetchAdminMe();

                // Αν ήδη έχει active business, πήγαινε dashboard
                if (me?.active_business_id) {
                    nav("/app/dashboard", { replace: true });
                    return;
                }

                const list = me?.businesses || [];
                if (mounted) setItems(list);

                // Αν έχει μόνο 1, auto-select για “premium UX”
                if (list.length === 1) {
                    setSaving(list[0].id);
                    await selectBusiness(list[0].id);
                    nav("/app/dashboard", { replace: true });
                    return;
                }
            } catch (e: any) {
                const msg = String(e?.message || "");
                if (msg === "UNAUTHORIZED") {
                    nav("/login", { replace: true });
                    return;
                }
                if (mounted) setError(msg || "ERROR");
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [nav]);

    const onPick = async (id: number) => {
        setError(null);
        setSaving(id);
        try {
            await selectBusiness(id);
            nav("/app/dashboard", { replace: true });
        } catch (e: any) {
            const msg = String(e?.message || "");
            if (msg === "UNAUTHORIZED") {
                nav("/login", { replace: true });
                return;
            }
            setError(msg || "ERROR");
            setSaving(null);
        }
    };

    return (
        <Box sx={{ maxWidth: 720, mx: "auto" }}>
            <Stack spacing={2.2}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 1000 }}>
                        {t("business.selectTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("business.selectSubtitle")}
                    </Typography>
                </Box>

                {loading ? (
                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography color="text.secondary">
                                    {t("common.loading")}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                ) : null}

                {error ? <Alert severity="error">{error}</Alert> : null}

                {!loading && items.length === 0 ? (
                    <Alert severity="warning">{t("business.noBusinesses")}</Alert>
                ) : null}

                {!loading && items.length > 0 ? (
                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography sx={{ fontWeight: 900, mb: 1 }}>
                                {t("business.yourBusinesses")}
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />

                            <Stack spacing={1}>
                                {items.map((b) => (
                                    <Stack
                                        key={b.id}
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        alignItems={{ xs: "stretch", sm: "center" }}
                                        sx={{
                                            p: 1.2,
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 900 }}>{b.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("business.roleLabel")}: {b.role} • {t("business.slugLabel")}:{" "}
                                                {b.slug}
                                            </Typography>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            onClick={() => onPick(b.id)}
                                            disabled={saving !== null}
                                        >
                                            {saving === b.id ? t("business.selecting") : t("business.selectCta")}
                                        </Button>
                                    </Stack>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                ) : null}
            </Stack>
        </Box>
    );
}
